/**
 * Sagamoon i18n Framework
 *
 * Type-safe internationalization with namespace-based translations.
 * Supports template variable interpolation ({{count}}, {{seconds}}, etc.)
 *
 * @example
 * import { useTranslation } from '@/locales';
 *
 * const { t } = useTranslation();
 * t('auth.login.title') // "Kirjaudu sisään"
 * t('story.loading.timeRemaining', { seconds: 30 }) // "Noin 30 sekuntia jäljellä"
 */

import fiTranslations from './fi.json';
import enTranslations from './en.json';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Supported languages
 */
export type Locale = 'fi' | 'en';

/**
 * Deep key extraction utility type
 * Converts nested object to dot-notation string union
 *
 * @example
 * type Keys = DeepKeys<{ auth: { login: { title: string } } }>
 * // Keys = "auth.login.title"
 */
type DeepKeys<T, Prefix extends string = ''> = T extends object
  ? {
      [K in keyof T & string]: T[K] extends object
        ? DeepKeys<T[K], `${Prefix}${K}.`>
        : `${Prefix}${K}`;
    }[keyof T & string]
  : never;

/**
 * All available translation keys (type-safe)
 * Auto-generated from fi.json structure
 */
export type TranslationKey = DeepKeys<typeof fiTranslations>;

/**
 * Template variables for interpolation
 */
export type TranslationVars = Record<string, string | number>;

/**
 * Translation function type
 */
export type TranslateFn = (key: TranslationKey, vars?: TranslationVars) => string;

// ============================================================================
// Translation Data
// ============================================================================

/**
 * Available translations by locale
 */
const translations: Record<Locale, typeof fiTranslations> = {
  fi: fiTranslations,
  en: enTranslations,
};

/**
 * Default locale (Finnish for beta)
 */
const DEFAULT_LOCALE: Locale = 'fi';

/**
 * Current active locale
 * TODO: Replace with React Context or Zustand store for dynamic language switching
 */
let currentLocale: Locale = DEFAULT_LOCALE;

// ============================================================================
// Core Translation Functions
// ============================================================================

/**
 * Get nested value from object using dot notation path
 *
 * @param obj - Object to search
 * @param path - Dot-notation path (e.g., "auth.login.title")
 * @returns Value at path or undefined
 *
 * @example
 * getNestedValue({ auth: { login: { title: "Login" } } }, "auth.login.title")
 * // Returns: "Login"
 */
function getNestedValue(obj: Record<string, unknown>, path: string): string | undefined {
  const result = path.split('.').reduce<unknown>((current, key) => {
    if (current && typeof current === 'object' && current !== null) {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
  return typeof result === 'string' ? result : undefined;
}

/**
 * Interpolate template variables in string
 * Replaces {{variable}} with values from vars object
 *
 * @param text - Text with template variables
 * @param vars - Variables to interpolate
 * @returns Interpolated text
 *
 * @example
 * interpolate("Hello {{name}}", { name: "World" })
 * // Returns: "Hello World"
 *
 * interpolate("{{count}} items", { count: 5 })
 * // Returns: "5 items"
 */
function interpolate(text: string, vars?: TranslationVars): string {
  if (!vars) return text;

  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const value = vars[key];
    return value !== undefined ? String(value) : match;
  });
}

/**
 * Get translation for key with optional template variables
 *
 * @param key - Translation key in dot notation
 * @param vars - Optional template variables
 * @param locale - Optional locale override (defaults to current locale)
 * @returns Translated text with interpolated variables
 *
 * @example
 * translate('auth.login.title')
 * // Returns: "Kirjaudu sisään"
 *
 * translate('story.loading.timeRemaining', { seconds: 30 })
 * // Returns: "Noin 30 sekuntia jäljellä"
 *
 * translate('auth.login.title', undefined, 'en')
 * // Returns: "Log in"
 */
export function translate(
  key: TranslationKey,
  vars?: TranslationVars,
  locale: Locale = currentLocale
): string {
  const translation = getNestedValue(translations[locale], key);

  if (!translation) {
    console.warn(`[i18n] Translation missing for key: "${key}" (locale: ${locale})`);
    return key; // Fallback to key itself
  }

  return interpolate(translation, vars);
}

/**
 * Set current locale
 *
 * @param locale - Locale to set
 *
 * @example
 * setLocale('en') // Switch to English
 * setLocale('fi') // Switch to Finnish (default)
 */
export function setLocale(locale: Locale): void {
  currentLocale = locale;
}

/**
 * Get current locale
 *
 * @returns Current active locale
 */
export function getLocale(): Locale {
  return currentLocale;
}

// ============================================================================
// React Hooks
// ============================================================================

/**
 * React hook for translations
 * Provides translation function and current locale
 *
 * @returns Translation function and locale
 *
 * @example
 * function LoginPage() {
 *   const { t, locale } = useTranslation();
 *
 *   return (
 *     <div>
 *       <h1>{t('auth.login.title')}</h1>
 *       <p>Current language: {locale}</p>
 *     </div>
 *   );
 * }
 */
export function useTranslation() {
  // TODO: Replace with React Context to enable re-renders on locale change
  const t: TranslateFn = (key, vars) => translate(key, vars);

  return {
    t,
    locale: currentLocale,
  };
}

/**
 * React hook for namespaced translations
 * Automatically prefixes all translation keys with namespace
 *
 * @param namespace - Namespace prefix (e.g., "auth", "story")
 * @returns Scoped translation function
 *
 * @example
 * function LoginForm() {
 *   const { t } = useNamespacedTranslation('auth.login');
 *
 *   return (
 *     <form>
 *       <h1>{t('title')}</h1> // Translates "auth.login.title"
 *       <input placeholder={t('email')} /> // Translates "auth.login.email"
 *     </form>
 *   );
 * }
 */
export function useNamespacedTranslation(namespace: string) {
  const t = (key: string, vars?: TranslationVars): string => {
    const fullKey = `${namespace}.${key}` as TranslationKey;
    return translate(fullKey, vars);
  };

  return {
    t,
    locale: currentLocale,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if translation exists for key
 *
 * @param key - Translation key to check
 * @param locale - Optional locale override
 * @returns True if translation exists
 */
export function hasTranslation(key: TranslationKey, locale: Locale = currentLocale): boolean {
  return getNestedValue(translations[locale], key) !== undefined;
}

/**
 * Get all available locales
 *
 * @returns Array of available locale codes
 */
export function getAvailableLocales(): Locale[] {
  return Object.keys(translations) as Locale[];
}

/**
 * Validate all translation keys exist in all locales
 * Useful for development/testing to ensure consistency
 *
 * @returns Array of missing translation keys by locale
 */
export function validateTranslations(): Array<{ locale: Locale; missingKeys: string[] }> {
  const results: Array<{ locale: Locale; missingKeys: string[] }> = [];

  // Get all keys from Finnish (reference locale)
  const getAllKeys = (obj: Record<string, unknown>, prefix = ''): string[] => {
    let keys: string[] = [];
    for (const key in obj) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        keys = keys.concat(getAllKeys(obj[key] as Record<string, unknown>, fullKey));
      } else {
        keys.push(fullKey);
      }
    }
    return keys;
  };

  const referenceKeys = getAllKeys(fiTranslations);

  // Check each locale for missing keys
  for (const locale of getAvailableLocales()) {
    const missingKeys: string[] = [];
    for (const key of referenceKeys) {
      if (!hasTranslation(key as TranslationKey, locale)) {
        missingKeys.push(key);
      }
    }
    if (missingKeys.length > 0) {
      results.push({ locale, missingKeys });
    }
  }

  return results;
}

// ============================================================================
// Type-Safe Dynamic Key Mappings
// ============================================================================

/**
 * Age band translation keys mapping
 * Provides type-safe translation for dynamic AgeBand values
 *
 * @example
 * import { AGE_BAND_TRANSLATION_KEYS } from '@/locales';
 * const label = t(AGE_BAND_TRANSLATION_KEYS[child.ageBand]);
 */
export const AGE_BAND_TRANSLATION_KEYS = {
  '3-4': 'common.ageBand.3-4',
  '5-6': 'common.ageBand.5-6',
  '7-8': 'common.ageBand.7-8',
  '9-10': 'common.ageBand.9-10',
} as const satisfies Record<string, TranslationKey>;

// GENRE_TRANSLATION_KEYS removed in Story 11.11 - genres replaced by themes

// ============================================================================
// Exports
// ============================================================================

// ============================================================================
// Story Prompt Utilities (Epic 13 Story 28)
// ============================================================================

/**
 * AgeBand type for story prompts
 */
type AgeBand = '3-4' | '5-6' | '7-8' | '9-10';

/**
 * Get all story prompts for a specific age band
 *
 * @param ageBand - Age band to get prompts for
 * @param locale - Optional locale override (defaults to current locale)
 * @returns Array of prompt strings for the age band
 *
 * @example
 * const prompts = getStoryPrompts('3-4');
 * // Returns: ["Pieni nalle...", "Liian kovaa karjuva...", ...]
 */
export function getStoryPrompts(ageBand: AgeBand, locale: Locale = currentLocale): string[] {
  const key = `ageBand_${ageBand}` as const;
  const localeData = translations[locale] as Record<string, unknown>;
  const storyPrompts = localeData.storyPrompts as Record<string, unknown> | undefined;

  if (!storyPrompts) {
    console.warn(`[i18n] storyPrompts namespace missing (locale: ${locale})`);
    return [];
  }

  const prompts = storyPrompts[key];
  return Array.isArray(prompts) ? (prompts as string[]) : [];
}

/**
 * Get a random story prompt for a specific age band
 *
 * @param ageBand - Age band to get prompt for
 * @param locale - Optional locale override (defaults to current locale)
 * @returns Random prompt string, or empty string if no prompts available
 *
 * @example
 * const prompt = getRandomStoryPrompt('5-6');
 * // Returns: "Keijut vierailevat salaa kaupungissa..."
 */
export function getRandomStoryPrompt(ageBand: AgeBand, locale: Locale = currentLocale): string {
  const prompts = getStoryPrompts(ageBand, locale);
  if (prompts.length === 0) return '';
  const randomIndex = Math.floor(Math.random() * prompts.length);
  return prompts[randomIndex];
}

export default translate;
export { fiTranslations, enTranslations };
